// src/admin/AdminDepositTasks.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';

export default function AdminDepositTasks() {
  const [statusFilter, setStatusFilter] = useState('open'); // open | requires_action | failed | all
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const qRef = useMemo(() => {
    const base = collection(db, 'depositTasks');
    if (statusFilter === 'all') {
      return query(base, orderBy('dueAt', 'asc'));
    }
    const statuses =
      statusFilter === 'open' ? ['pending'] :
      statusFilter === 'requires_action' ? ['requires_action'] :
      statusFilter === 'failed' ? ['reauth_failed', 'missing_payment_method'] : ['pending'];

    // where('status','in', [...]) + orderBy('dueAt') requires a composite index (see below)
    return query(base, where('status', 'in', statuses), orderBy('dueAt', 'asc'));
  }, [statusFilter]);

  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(qRef, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setRows(data);
      setLoading(false);
    });
    return () => unsub();
  }, [qRef]);

  const markDone = async (id) => {
    await updateDoc(doc(db, 'depositTasks', id), { status: 'done', doneAt: new Date() });
  };

  const reauthNow = async (task) => {
    // optional callable that forces an immediate reauth on a single booking
    const fn = httpsCallable(functions, 'reauthDepositNow'); // implement server-side
    await fn({ bookingId: task.bookingId });
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <h2 style={styles.title}>Deposit Tasks</h2>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={styles.select}>
          <option value="open">Open</option>
          <option value="requires_action">Requires Action</option>
          <option value="failed">Failed</option>
          <option value="all">All</option>
        </select>
      </div>

      {loading ? (
        <p style={{ padding: '1rem' }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ padding: '1rem' }}>No tasks.</p>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th>Booking</th>
              <th>Status</th>
              <th>Amount</th>
              <th>Due</th>
              <th>Reason</th>
              <th style={{ width: 220 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: 600 }}>{r.bookingId}</span>
                    <small>Renter: {r.renterId} • Host: {r.hostId}</small>
                  </div>
                </td>
                <td><Badge status={r.status} /></td>
                <td>${((r.amountCents || 0) / 100).toFixed(2)}</td>
                <td>{r.dueAt?.toDate ? r.dueAt.toDate().toLocaleString() : '-'}</td>
                <td>{r.reason || '-'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {r.status === 'requires_action' && (
                      <button style={styles.btnPrimary} onClick={() => reauthNow(r)}>Re-auth Now</button>
                    )}
                    <a style={styles.btn} href={`/admin/bookings/${r.bookingId}`} rel="noreferrer">Open Booking</a>
                    <button style={styles.btn} onClick={() => markDone(r.id)}>Mark done</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Badge({ status }) {
  const map = {
    pending: { bg: '#FEF3C7', fg: '#92400E', text: 'Pending' },
    requires_action: { bg: '#DBEAFE', fg: '#1E40AF', text: 'Requires Action' },
    reauth_failed: { bg: '#FEE2E2', fg: '#991B1B', text: 'Failed' },
    missing_payment_method: { bg: '#FEE2E2', fg: '#991B1B', text: 'Missing PM' },
    ok: { bg: '#DCFCE7', fg: '#166534', text: 'OK' },
    done: { bg: '#E5E7EB', fg: '#374151', text: 'Done' },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{
      background: s.bg, color: s.fg, padding: '2px 8px', borderRadius: 999,
      fontSize: 12, fontWeight: 700
    }}>{s.text}</span>
  );
}

const styles = {
  wrap: { padding: '1rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  title: { margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#065f46' },
  select: { padding: '6px 8px', borderRadius: 8, border: '1px solid #d1d5db' },
  table: {
    width: '100%', borderCollapse: 'separate', borderSpacing: 0,
    border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden'
  },
  btn: {
    background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
    padding: '6px 10px', cursor: 'pointer'
  },
  btnPrimary: {
    background: '#22c55e', color: '#fff', border: '1px solid #16a34a',
    borderRadius: 6, padding: '6px 10px', cursor: 'pointer', fontWeight: 700
  }
};
